import React from 'react';
import { calculateTotalImpact } from '../../utils/calculationHelpers';
import { SCENARIO_CONFIGS, getScenarioConfig } from '../../config/scenarioConfigs';
import { SCENARIO_DESCRIPTIONS } from '../../config/scenarioDescriptions';
import { SCENARIO_LOOKUP,getScenarioLookup  } from '../../config/scenarioConfigs';

// Panel scenario ID that has its own separate button
const PANEL_SCENARIO_ID = 'net_zero_futures_dvl_shift_high_transstshiftec_2001';

const QuickChoicesPanel = ({
  sectorImpact,
  updateSectorImpact,
  setIsQuickChoicesOpen,
  ambitionLevel,
  setAmbitionLevel
}) => {
  // Get current scenario from sectorImpact
  const getCurrentScenario = () => {
    for (const [scenarioName, scenarioVariants] of Object.entries(SCENARIO_CONFIGS)) {
      for (const [variant, config] of Object.entries(scenarioVariants)) {
        const isMatch = Object.entries(config).every(([sector, impact]) =>
          sectorImpact[sector] === impact
        );
        if (isMatch) {
          return { scenario: scenarioName, ambition: variant };
        }
      }
    }
    return { scenario: 'current', ambition: 'low' };
  };

  const currentSelection = getCurrentScenario();

  const handleScenarioSelect = (scenario) => {
    const scenarioConfig = getScenarioConfig(scenario, ambitionLevel);
    updateSectorImpact(scenarioConfig);
    setIsQuickChoicesOpen(false);
  };

  const handleAmbitionChange = (ambition) => {
    setAmbitionLevel(ambition);
  };
  const AMBITION_LABEL = { low: 'Less', high: 'More' };
const AMBITION_HELP  = {
  low:  'Smaller, slower changes in all areas discussed by the panel',
  high: 'Larger, faster changes in all areas discussed by the panel',
};

  return (
    <div
      className={`fixed top-0 right-0 w-96 h-full bg-white shadow-2xl transform transition-transform duration-300 ease-in-out
      z-[100] overflow-y-auto p-6`}
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Quick Scenario Selection</h2>
        <button
          onClick={() => setIsQuickChoicesOpen(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>
              {/* 4th button underneath */}
          <button
            type="button"
            onClick={() =>
              handleScenarioSelect(PANEL_SCENARIO_ID)
            }
            className="mt-3 p-3 rounded-lg border transition-all duration-200 bg-orange-100 text-left text-sm text-orange-800 ring-orange-300 hover:bg-orange-200 mb-3" 
          >
          <div className="flex justify-between items-center mb-1">
              <h4 className="text-xs font-semibold capitalize">
              Final citizens’ panel scenario
              </h4>
            </div>

            <p className="text-xs text-gray-600">
              More support measures, enabling faster change and sustaining the rate of change over time in all areas covered by the panel. Moderate levels of action to reduce energy demand in areas the panel didn’t cover.  More nature-based Carbon Dioxide Removal and less engineered removals
            </p>
          </button>

      <div className="space-y-6">
        {/* Step 1: Ambition Level Selection */}
        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="text-sm font-medium mb-3">Step 1: Choose Your Panel-led Scenario</h3>
          <div className="flex space-x-2">
            <div className="flex space-x-2">
      {['low', 'high'].map((ambition) => (
        <button
          key={ambition}
          type="button"
          onClick={() => handleAmbitionChange(ambition)} // <- use your actual handler
          aria-pressed={ambitionLevel === ambition}
          className={`flex-1 p-3 rounded-lg border transition-all duration-200 text-left text-sm
            ${ambitionLevel === ambition
              ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-200'
              : 'bg-white hover:bg-gray-50 border-gray-200'}
          `}
        >
          <div className="font-medium">{AMBITION_LABEL[ambition]} Change</div>
          <div className="text-xs text-gray-500 mt-1">
            {AMBITION_HELP[ambition]}
          </div>
        </button>
      ))}
    </div>
          </div>
        </div>

        {/* Step 2: Scenario Selection */}
        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="text-sm font-medium mb-3">Step 2: Select background assumptions from PLEF scenarios</h3>
          <div className="grid grid-cols-1 gap-2">
            {Object.keys(SCENARIO_CONFIGS).filter(scenario => 
              scenario !== 'current' && 
              scenario !== PANEL_SCENARIO_ID
            ).map((scenario) => {
              const { description } = SCENARIO_DESCRIPTIONS[scenario]?.[ambitionLevel] ||
                                     SCENARIO_DESCRIPTIONS[scenario]?.current ||
                                     { description: 'Explore this scenario pathway' };

              const isActive = currentSelection.scenario === scenario &&
                              currentSelection.ambition === ambitionLevel;

const node =
  SCENARIO_DESCRIPTIONS?.[scenario]?.[ambitionLevel] ??
  SCENARIO_DESCRIPTIONS?.[scenario]?.current ??
  {};

const panelText =
  node.panel_description ??
  node.description ??
  'Explore this scenario pathway';

              // Predefined color classes for scenarios
              const colorClasses = {
                current: {
                  bg: 'bg-gray-100',
                  text: 'text-gray-800',
                  ring: 'ring-gray-300',
                },
                steer: {
                  bg: 'bg-blue-100',
                  text: 'text-blue-800',
                  ring: 'ring-blue-300'
                },
                shift: {
                  bg: 'bg-green-100',
                  text: 'text-green-800',
                  ring: 'ring-green-300'
                },
                transform: {
                  bg: 'bg-purple-100',
                  text: 'text-purple-800',
                  ring: 'ring-purple-300'
                }
              }[scenario];

              return (
                <button
                  key={scenario}
                  onClick={() => {
                    handleScenarioSelect(scenario);
                  }}
                  className={`p-3 rounded-lg border transition-all duration-200 text-left
                    ${isActive
                      ? `ring-2 ring-offset-1 ${colorClasses.ring} ${colorClasses.bg}`
                      : `${colorClasses.bg} hover:bg-${colorClasses.bg.replace('bg-', '')}-200`}
                    ${colorClasses.text}`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <h4 className="text-xs font-semibold capitalize">
                      {scenario} Scenario
                    </h4>
                    {isActive && (
                      <span className="text-xs bg-green-500 text-white px-1 py-0.5 rounded">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    {panelText}
                  </p>
                </button>
              );
            })}

            
          </div>
   
        </div>


      </div>
    </div>
  );
};

export default QuickChoicesPanel;
